import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { jwtDecode } from 'jwt-decode';
import { AuthProvider as AuthProviderV2, IS_OFFLINE } from '@/common/auth/authProvider';
import { getUrls } from '@/common/composables/url/url';
import { fetchMe } from '@/api/auth';
import type {
  MemberAccountSummary,
  MyKeySummary,
  OwnedAccountSummary,
  Persona,
  UserRole,
} from '@/types/parley';

interface JwtPayload {
  sub?: string;
  scoped_uid?: string;
  email?: string;
  name?: string;
  first_name?: string;
  last_name?: string;
}

interface PeopleSearchPerson {
  krbName: string;
  preferredFirstName: string;
  preferredLastName: string;
  mitEmail: string;
  profilePictureUrl?: string;
  staff?: boolean;
  gradStudent?: boolean;
}

export const useAuthStore = defineStore('auth', () => {
  const isAuthenticated = ref(false);
  const kerberos = ref('');
  const email = ref('');
  const firstName = ref('');
  const isStaff = ref<boolean>(false);
  const lastName = ref('');
  const profilePictureUrl = ref<string | null>(null);
  const role = ref<UserRole>('user');
  const permissions = ref<string[]>([]);
  const ownedAccounts = ref<OwnedAccountSummary[]>([]);
  const adminAccounts = ref<OwnedAccountSummary[]>([]);
  const approverAccounts = ref<OwnedAccountSummary[]>([]);
  const memberAccounts = ref<MemberAccountSummary[]>([]);
  const myKeys = ref<MyKeySummary[]>([]);
  const roleLoaded = ref(false);

  const isImpersonated = ref(false);
  const impersonatedBy = ref('');

  const displayName = computed(() => {
    if (firstName.value && lastName.value) return `${firstName.value} ${lastName.value}`;
    if (kerberos.value) return kerberos.value;
    return '';
  });

  const userInitials = computed(() => {
    if (firstName.value && lastName.value) {
      return `${firstName.value[0]}${lastName.value[0]}`.toUpperCase();
    }
    if (kerberos.value) return kerberos.value[0].toUpperCase();
    return '?';
  });

  const isSuperAdmin = computed(() => role.value === 'super_admin');
  const isSupport = computed(() => role.value === 'support');
  const isAdmin = computed(() => role.value === 'admin');
  const isUser = computed(() => role.value === 'user');

  // The view-only support role sees every staff surface a super_admin does but
  // must not mutate anything. Components on those surfaces consult this flag to
  // disable their write controls. It does NOT affect a support user's own
  // My Keys / My Accounts, which are gated by ownership, not role.
  const isReadOnly = computed(() => isSupport.value);

  const ROLE_LABELS: Record<UserRole, string> = {
    super_admin: 'Super Administrator',
    support: 'Support',
    admin: 'Account Admin',
    user: 'User',
  };
  const roleLabel = computed(() => ROLE_LABELS[role.value] ?? 'User');
  // Write/manage capabilities — deliberately super-admin-only and therefore
  // false for support (which can view config but not change it).
  const canManageAllAccounts = computed(() => isSuperAdmin.value);
  const canAccessSystemConfig = computed(() => isSuperAdmin.value);

  const hasOwnedAccounts = computed(() => ownedAccounts.value.length > 0);
  const hasManagedAccounts = computed(
    () => ownedAccounts.value.length > 0 || adminAccounts.value.length > 0
  );
  const managedAccounts = computed<OwnedAccountSummary[]>(() => {
    const byId = new Map<string, OwnedAccountSummary>();
    for (const account of ownedAccounts.value) byId.set(account.accountId, account);
    for (const account of adminAccounts.value)
      if (!byId.has(account.accountId)) byId.set(account.accountId, account);
    for (const account of approverAccounts.value)
      if (!byId.has(account.accountId))
        byId.set(account.accountId, { ...account, role: 'Approver' });
    return Array.from(byId.values());
  });
  const hasMemberAccounts = computed(() => memberAccounts.value.length > 0);
  const hasKeys = computed(() => myKeys.value.length > 0);

  function isApproverOnlyAccount(accountId: string | null | undefined): boolean {
    if (!accountId) return false;
    if (ownedAccounts.value.some((a) => a.accountId === accountId)) return false;
    if (adminAccounts.value.some((a) => a.accountId === accountId)) return false;
    return approverAccounts.value.some((a) => a.accountId === accountId);
  }

  /**
   * Resolves which dashboard the user should land on. Staff roles
   * (super_admin / admin) go to the full dashboard; everyone else lands on
   * the key-holder view, which renders an empty-state if they have no keys yet.
   */
  const primaryPersona = computed<Persona>(() => {
    if (role.value === 'super_admin') return 'super_admin';
    if (role.value === 'support') return 'support';
    if (role.value === 'admin') return 'admin';
    if (hasManagedAccounts.value) return 'account_admin';
    return 'key_holder';
  });

  async function fetchProfile(username: string) {
    // People Search lives on the MIT API gateway, which is unreachable offline.
    // Skipping it costs only isStaff (grad-student detection): name, email and
    // the profile picture all arrive from /v1/auth/me, which fetchRole has
    // already applied.
    if (IS_OFFLINE) return;

    try {
      const token = await AuthProviderV2.getToken();
      const { globalApiUrlBase } = getUrls();
      const response = await fetch(
        `${globalApiUrlBase}/people-search/v1/lookup?sid=${encodeURIComponent(username)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!response.ok) return;

      const data = await response.json();
      const person: PeopleSearchPerson | undefined = data.people?.[0];
      if (!person) return;

      firstName.value = person.preferredFirstName || firstName.value;
      lastName.value = person.preferredLastName || lastName.value;
      email.value = person.mitEmail || email.value;
      // profilePictureUrl is deliberately NOT set here. This call uses the
      // browser's own live Okta token, and under a standard Okta impersonation
      // session that token's identity IS the impersonated target — so this
      // becomes a self-lookup by whatever privilege level the target happens
      // to have, not the admin's, and can silently come back without a
      // picture even though the backend (which resolves it server-side with
      // Parley's own People Search credential) has one. /v1/auth/me is the
      // only source of truth for it; see fetchRole.
      // Grad students are treated as staff for account-creation purposes
      // (mirrors the backend's affiliation rule in account-service.ts),
      // so they get the same Add Account access as real MIT staff.
      isStaff.value = !!person?.staff || !!person?.gradStudent;
    } catch (error) {
      console.warn('Failed to fetch profile from People Search:', error);
    }
  }

  async function fetchRole() {
    try {
      const me = await fetchMe();
      role.value = me.role;
      permissions.value = me.permissions;
      ownedAccounts.value = me.ownedAccounts ?? [];
      adminAccounts.value = me.adminAccounts ?? [];
      memberAccounts.value = me.memberAccounts ?? [];
      approverAccounts.value = me.approverAccounts ?? [];
      myKeys.value = me.myKeys ?? [];
      // /v1/auth/me is authoritative for identity. When impersonating, the
      // JWT decode set kerberos to the real caller; backend returns the
      // impersonated kerb here. Overwriting unconditionally keeps the user
      // chip + downstream profile lookup in sync with whoever the server
      // thinks we are.
      if (me.kerberos) {
        kerberos.value = me.kerberos;
      }
      // Use backend display name if available and we don't have one from People Search
      if (me.displayName) {
        const parts = me.displayName.split(' ');
        firstName.value = parts[0] || '';
        lastName.value = parts.slice(1).join(' ') || '';
      }
      if (me.email) {
        email.value = me.email || email.value;
      }
      // Resolved server-side (Parley's own People Search credential), not by
      // this browser's own Okta token — see fetchProfile for why that
      // distinction matters under impersonation.
      profilePictureUrl.value = me.profilePictureUrl ?? null;
      isImpersonated.value = me.isImpersonated ?? false;
      impersonatedBy.value = me.impersonatedBy ?? '';
      roleLoaded.value = true;
    } catch (error) {
      // Backend auth/me call failed — default to minimal 'user' access
      console.warn('Failed to fetch role from /v1/auth/me, defaulting to user:', error);
      role.value = 'user';
      permissions.value = ['accounts:read', 'keys:read', 'logs:read'];
      ownedAccounts.value = [];
      adminAccounts.value = [];
      memberAccounts.value = [];
      approverAccounts.value = [];
      myKeys.value = [];
      isImpersonated.value = false;
      impersonatedBy.value = '';
      roleLoaded.value = true;
    }
  }

  async function initializeFromToken() {
    try {
      const token = await AuthProviderV2.getToken();
      if (!token) return;

      const decoded = jwtDecode<JwtPayload>(token);
      const jwtKerb = decoded.scoped_uid?.split('@')[0] ?? decoded.email?.split('@')[0] ?? '';
      kerberos.value = jwtKerb;
      email.value = decoded.email ?? '';
      firstName.value = decoded.first_name ?? '';
      lastName.value = decoded.last_name ?? '';
      isAuthenticated.value = true;

      // fetchRole has to land first so kerberos.value reflects the *server's*
      // view of the caller (which, under impersonation, may be a different
      // kerb than the JWT carries). Once that's settled, look up the profile
      // for whoever we actually are — same code path for real and impersonated
      // sessions; no branching.
      await fetchRole();
      if (kerberos.value) {
        await fetchProfile(kerberos.value);
      }
    } catch (error) {
      console.warn('Failed to decode JWT:', error);
    } finally {
      // WEB-006: the route guard blocks on `roleLoaded`, so initialization MUST
      // always settle it — otherwise a missing token (early return) or a
      // malformed/expired token (decode throw) leaves guarded navigation waiting
      // forever. On those paths the user stays unauthenticated (role defaults to
      // 'user') and App.vue / AuthProviderV2 drives the Okta sign-in redirect;
      // settling here just unblocks the guard instead of deadlocking it.
      roleLoaded.value = true;
    }
  }

  async function signOut() {
    isAuthenticated.value = false;
    kerberos.value = '';
    email.value = '';
    firstName.value = '';
    lastName.value = '';
    profilePictureUrl.value = null;
    role.value = 'user';
    permissions.value = [];
    ownedAccounts.value = [];
    adminAccounts.value = [];
    approverAccounts.value = [];
    memberAccounts.value = [];
    myKeys.value = [];
    isImpersonated.value = false;
    impersonatedBy.value = '';
    roleLoaded.value = false;
  }

  return {
    isAuthenticated,
    kerberos,
    email,
    firstName,
    lastName,
    isStaff,
    profilePictureUrl,
    displayName,
    userInitials,
    role,
    permissions,
    ownedAccounts,
    adminAccounts,
    approverAccounts,
    managedAccounts,
    memberAccounts,
    myKeys,
    isImpersonated,
    impersonatedBy,
    roleLoaded,
    isSuperAdmin,
    isSupport,
    isReadOnly,
    isAdmin,
    isUser,
    roleLabel,
    hasOwnedAccounts,
    hasManagedAccounts,
    hasMemberAccounts,
    hasKeys,
    primaryPersona,
    canManageAllAccounts,
    canAccessSystemConfig,
    isApproverOnlyAccount,
    initializeFromToken,
    fetchRole,
    signOut,
  };
});
